import React, { useMemo, useState } from "react";
import Select from "react-select";
import { ChevronDown, ChevronUp, Filter, RotateCcw, Search } from "lucide-react";
import { DATE_PRESETS } from "../../utils/analyticsDates";

const EXTRA_ORDER_STATUSES = [
  { id: "Printing Hold", name: "Printing Hold" },
  { id: "Girabatcom", name: "Girabatcom" },
  { id: "Printed", name: "Printed" },
];

const selectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: 12,
    borderColor: "#e2e8f0",
    minHeight: 42,
    fontSize: 14,
  }),
  menu: (base) => ({ ...base, zIndex: 40 }),
};

/**
 * Filter panel — date presets, multi-selects, apply/clear.
 */
export default function AnalyticsFilters({
  draft,
  setDraft,
  options,
  onApply,
  onClear,
  isFetching,
}) {
  const statusOptions = useMemo(() => {
    const fromApi = options?.statuses || [];
    const seen = new Set(fromApi.map((s) => String(s.id).toLowerCase()));
    const merged = [
      ...fromApi,
      ...EXTRA_ORDER_STATUSES.filter((s) => !seen.has(s.id.toLowerCase())),
    ];
    return merged.map((s) => ({ value: s.id, label: s.name }));
  }, [options]);
  const confirmOptions = useMemo(
    () =>
      (options?.confirmationCategories || []).map((s) => ({
        value: s.id,
        label: s.name,
      })),
    [options]
  );
  const categoryOptions = useMemo(
    () => (options?.categories || []).map((s) => ({ value: s.id, label: s.name })),
    [options]
  );
  const brandOptions = useMemo(
    () => (options?.brands || []).map((s) => ({ value: s.id, label: s.name })),
    [options]
  );
  const typeOptions = useMemo(
    () => (options?.types || []).map((s) => ({ value: s.id, label: s.name })),
    [options]
  );
  const stateOptions = useMemo(
    () => (options?.states || []).map((s) => ({ value: s, label: s })),
    [options]
  );
  const cityOptions = useMemo(() => {
    const cities = options?.cities || [];
    const filtered = draft.state
      ? cities.filter((c) => c.state === draft.state)
      : cities;
    return filtered.map((c) => ({
      value: c.city,
      label: draft.state ? c.city : `${c.city} — ${c.state}`,
    }));
  }, [options, draft.state]);

  const couponOptions = [
    { value: "", label: "الكل" },
    { value: "true", label: "نعم — استُخدم كوبون" },
    { value: "false", label: "لا — بدون كوبون" },
  ];

  const update = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
  const [mobileOpen, setMobileOpen] = useState(false);

  const activeFilterCount =
    (draft.statuses?.length || 0) +
    (draft.confirmationCategories?.length || 0) +
    (draft.categoryIds?.length || 0) +
    (draft.brandIds?.length || 0) +
    (draft.typeIds?.length || 0) +
    (draft.state ? 1 : 0) +
    (draft.city ? 1 : 0) +
    (draft.couponUsed !== null ? 1 : 0) +
    (draft.preset !== "thisMonth" ? 1 : 0);

  const handleApply = () => {
    onApply();
    setMobileOpen(false);
  };

  return (
    <aside className="relative z-20 bg-white rounded-xl md:rounded-2xl border border-slate-200 shadow-sm p-2.5 md:p-5 space-y-3 md:space-y-4 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-slate-800 font-bold text-sm md:text-lg min-w-0">
          <Filter size={16} className="text-indigo-600 shrink-0" />
          التصفية
          {activeFilterCount > 0 && (
            <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-indigo-100 px-1.5 text-[11px] font-bold text-indigo-700">
              {activeFilterCount}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setMobileOpen((open) => !open)}
          className="lg:hidden inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-700"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? "إخفاء" : "إظهار"}
          {mobileOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      <div className={`${mobileOpen ? "block" : "hidden"} lg:block space-y-4`}>
      {/* Date presets */}
      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-2">الفترة الزمنية</label>
        <div className="flex flex-wrap gap-1.5">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() =>
                update({
                  preset: p.id,
                  // Year ranges are clearer (and lighter) as monthly buckets
                  ...(p.id === "thisYear" || p.id === "lastYear"
                    ? { granularity: "monthly" }
                    : {}),
                })
              }
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition ${
                draft.preset === p.id
                  ? "bg-indigo-600 text-white shadow"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {draft.preset === "custom" && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-slate-500 mb-1">من</label>
            <input
              type="date"
              value={draft.customFrom || ""}
              onChange={(e) => update({ customFrom: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">إلى</label>
            <input
              type="date"
              value={draft.customTo || ""}
              onChange={(e) => update({ customTo: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
        </div>
      )}

      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1">حالة الطلب</label>
        <Select
          isMulti
          options={statusOptions}
          styles={selectStyles}
          placeholder="كل الحالات"
          value={statusOptions.filter((o) => draft.statuses.includes(o.value))}
          onChange={(selected) =>
            update({ statuses: (selected || []).map((s) => s.value) })
          }
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1">
          Confirmation Category
        </label>
        <Select
          isMulti
          options={confirmOptions}
          styles={selectStyles}
          placeholder="Normal / Custom / Deposit"
          value={confirmOptions.filter((o) =>
            draft.confirmationCategories.includes(o.value)
          )}
          onChange={(selected) =>
            update({
              confirmationCategories: (selected || []).map((s) => s.value),
            })
          }
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1">التصنيف</label>
        <Select
          isMulti
          options={categoryOptions}
          styles={selectStyles}
          placeholder="كل التصنيفات"
          value={categoryOptions.filter((o) => draft.categoryIds.includes(o.value))}
          onChange={(selected) =>
            update({ categoryIds: (selected || []).map((s) => s.value) })
          }
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1">البراند</label>
        <Select
          isMulti
          options={brandOptions}
          styles={selectStyles}
          placeholder="كل البراندات"
          value={brandOptions.filter((o) => draft.brandIds.includes(o.value))}
          onChange={(selected) =>
            update({ brandIds: (selected || []).map((s) => s.value) })
          }
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1">النوع</label>
        <Select
          isMulti
          options={typeOptions}
          styles={selectStyles}
          placeholder="كل الأنواع"
          value={typeOptions.filter((o) => draft.typeIds.includes(o.value))}
          onChange={(selected) =>
            update({ typeIds: (selected || []).map((s) => s.value) })
          }
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1">المحافظة</label>
        <Select
          isClearable
          options={stateOptions}
          styles={selectStyles}
          placeholder="كل المحافظات"
          value={stateOptions.find((o) => o.value === draft.state) || null}
          onChange={(selected) =>
            update({ state: selected?.value || "", city: "" })
          }
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1">المدينة</label>
        <Select
          isClearable
          options={cityOptions}
          styles={selectStyles}
          placeholder="كل المدن"
          value={cityOptions.find((o) => o.value === draft.city) || null}
          onChange={(selected) => update({ city: selected?.value || "" })}
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1">استخدام كوبون</label>
        <Select
          options={couponOptions}
          styles={selectStyles}
          value={
            couponOptions.find(
              (o) =>
                o.value ===
                (draft.couponUsed === true
                  ? "true"
                  : draft.couponUsed === false
                    ? "false"
                    : "")
            ) || couponOptions[0]
          }
          onChange={(selected) => {
            const v = selected?.value;
            update({
              couponUsed: v === "true" ? true : v === "false" ? false : null,
            });
          }}
        />
      </div>

      <div className="flex gap-2 pt-2 sticky bottom-0 bg-white pb-1">
        <button
          type="button"
          onClick={handleApply}
          disabled={isFetching}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 text-sm shadow disabled:opacity-60"
        >
          <Search size={16} />
          تطبيق
        </button>
        <button
          type="button"
          onClick={onClear}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 font-semibold px-4 py-2.5 text-sm"
        >
          <RotateCcw size={16} />
          مسح
        </button>
      </div>
      </div>
    </aside>
  );
}
